import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TeButton,
  TeButtonMinimal,
  TeContainer,
  TeContainerSlim,
  TeContextMenu,
  TeContextMenuControl,
  TeContextMenuItem,
  TeDivider,
  TeIcon,
  TeTag,
  TeText,
} from '@topcon/tuicl-react';
import { PlaceholderContainer, useNotification } from '@topcon/topcon-utils';
import { Statuses } from '@topcon/tuicl/dist/common/component-types';
import { styled } from 'styled-components';

import { Section } from '../../../utils/styling';
import Header from '../../../components/Header';
import DeleteDomainModal from './DeleteDomainModal';
import AddEditDomainModal from './AddEditDomainModal';
import { useVerifyDomain } from './domainRegistration.mutations';
import { useDomains } from './domainRegistration.queries';
import { useIdentityProviders } from '../IdentityProviders/identityProviders.queries';
import { copyToClipboard } from '../../../utils/utils';
import { border, common, text } from '../../../providers/theme/theme.index';
import { DomainBody } from '../../../api/generated/pap';
import { useErrorDrawer } from '../../../providers/ErrorDrawerContext/error-drawer.context';

const DomainRegistration = () => {
  const { t } = useTranslation('settings');
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const [selectedDomain, setSelectedDomain] = useState<DomainBody | null>(null);
  const [addEditDomainModalOpen, setAddEditDomainModalOpen] = useState(false);
  const [deleteDomainModalOpen, setDeleteDomainModalOpen] = useState(false);
  const [testing, setTesting] = useState('');
  const { data: domainsResponse, isFetched: loaded, refetch: refetchDomains } = useDomains();
  const { data: identityProvidersResponse, isFetched: loadedIdentityProviders } =
    useIdentityProviders();
  const { mutateAsync: verifyDomain } = useVerifyDomain();
  const { items: domains } = domainsResponse ?? { items: [] };
  const { items: identityProviders } = identityProvidersResponse ?? { items: [] };
  const noContent = !loaded || !domains.length || !loadedIdentityProviders;

  const openDomainAddEditModal = async (domain: DomainBody | null) => {
    setSelectedDomain(domain);
    setAddEditDomainModalOpen(true);
  };

  const handleAddEditDomainModalClose = (shouldUpdate: boolean) => {
    setAddEditDomainModalOpen(false);
    setSelectedDomain(null);
    if (shouldUpdate) refetchDomains();
  };

  const openDomainDeleteModal = async (domain: DomainBody) => {
    setSelectedDomain(domain);
    setDeleteDomainModalOpen(true);
  };

  const handleDeleteDomainModalClose = (shouldUpdate: boolean) => {
    setDeleteDomainModalOpen(false);
    setSelectedDomain(null);
    if (shouldUpdate) refetchDomains();
  };

  // TODO: need something to test the actual domain (something with an actual txt record)
  //       - now the query succeeds but the verification fails
  // TODO: Should we have more detailed error messages or is is enough to log the details?
  const testDomain = async (id: string, name: string) => {
    setTesting(id);
    try {
      setSelectedDomain({ id } as DomainBody);
      const res = await verifyDomain({ id });
      // TODO: This one could probably mention the cause of the error, but the messages are only english
      if (!res.verified)
        throw new Error(`Failed to verify domain: ${res.verifyErrors?.join(', ')}`);
      setTesting('');
      refetchDomains();
    } catch (error) {
      if (!showError(error)) {
        notificationService.addNotification(
          t('domainVerificationFailed'),
          t('domainVerificationFailedDesc', { name }),
          Statuses.error
        );
      }
      setTesting('');
      console.error('Domain verification failed', error);
    }
  };

  const getIdentityProviderInfo = (id: string) => {
    const provider = identityProviders.find((provider) => provider.id === id);
    if (provider?.OIDCConfig) return provider.OIDCConfig.issuerURL;
    if (provider?.SAMLConfig) return provider.SAMLConfig.metaDataURL;
    return '';
  };

  const renderEmptyState = () => {
    return (
      <PlaceholderContainer
        icon="language"
        text={t('domainRegistration.noDomainsYet')}
        secondaryText={t('clickButtonToAddOne')}
      >
        <TeButton
          text={t('domainRegistration.addDomain')}
          size="s"
          teclick={() => openDomainAddEditModal(null)}
        />
      </PlaceholderContainer>
    );
  };

  const renderLoader = () => {
    return <PlaceholderContainer hasLoader text={t('domainRegistration.loadingDomains')} />;
  };

  const renderNoContent = () => {
    return (
      <TeContainerSlim
        backgroundColor="background.secondary"
        borderRadius={4}
        fill
        justifyContent="center"
        alignItems="center"
      >
        {!loaded ? renderLoader() : renderEmptyState()}
      </TeContainerSlim>
    );
  };

  const renderDomains = () => {
    return domains.map((domain) => {
      return (
        <TeContainerSlim
          key={domain.proofTXT}
          column
          fill
          backgroundColor="background.secondary"
          borderRadius={4}
        >
          <TeContainer fill justifyContent="space-between" alignItems="center">
            <TeContainerSlim gap="s" alignItems="center">
              <TeIcon name="language" size="l" />
              <TeText size={16} weight={700}>
                {domain.name}
              </TeText>
            </TeContainerSlim>
            <TeContextMenuControl contextId={`domain${domain.id}`}>
              <TeButtonMinimal icon="more_vert" size="s" variant="neutral" />
            </TeContextMenuControl>
            <TeContextMenu contextId={`domain${domain.id}`} placement="bottomLeft" size="s">
              <TeContextMenuItem text={t('edit')} teclick={() => openDomainAddEditModal(domain)} />
              <TeContextMenuItem
                textColor={text.error.primary}
                text={t('delete')}
                teclick={() => openDomainDeleteModal(domain)}
              />
            </TeContextMenu>
          </TeContainer>

          <TeDivider dividerColor="border.secondary" />

          <TeContainer column fill>
            <TeContainer
              fill
              height={32}
              alignItems="center"
              backgroundColor="background.tertiary"
              gap="m"
            >
              <TeText size={12}>{t('domainRegistration.identityProvider')}</TeText>
              {domain.identityProviderId ? (
                <TeText weight={500}>{getIdentityProviderInfo(domain.identityProviderId)}</TeText>
              ) : (
                <TeText weight={500} color="text.secondary">
                  {t('domainRegistration.noIdentityProvider')}
                </TeText>
              )}
            </TeContainer>

            <TeContainerSlim fill justifyContent="space-between">
              <TeContainerSlim
                padding={[0, 's']}
                height={32}
                borderRadius={4}
                gap="xs"
                backgroundColor="background.primary"
                alignItems="center"
                borderColor="border.secondary"
              >
                <TeContainerSlim gap="xs2" alignItems="center">
                  <TeIcon name="info" />
                  <TeText size={12}>{t('domainRegistration.txtRecord')}</TeText>
                </TeContainerSlim>
                <TeText weight={700}>{`"${domain.proofTXT}"`}</TeText>
                <ButtonDivider />
                <TeButtonMinimal
                  size="xs"
                  icon="content_copy"
                  text={t('copy')}
                  teclick={() => copyToClipboard(domain.proofTXT)}
                />
              </TeContainerSlim>

              <TeContainerSlim alignItems="center" gap="xs">
                <TeText size={12}>{t('domainRegistration.status')}</TeText>
                <TeContainerSlim height={34} alignItems="center">
                  {domain.verified ? (
                    <TeTag
                      backgroundColor="background.success.quaternary"
                      icon="check_circle_outline"
                      textColor="text.inverse"
                      text={t('domainRegistration.verified')}
                      size="s"
                    />
                  ) : (
                    <TeTag
                      backgroundColor="text.warning.primary"
                      icon="info"
                      textColor="text.inverse"
                      text={t('domainRegistration.pending')}
                      size="s"
                    />
                  )}
                </TeContainerSlim>

                <Divider />

                <TeButton
                  text={t('domainRegistration.reTestDNS')}
                  size="s"
                  variant="neutral"
                  teclick={() => testDomain(domain.id, domain.name)}
                  loading={testing === domain.id}
                />
              </TeContainerSlim>
            </TeContainerSlim>
          </TeContainer>
        </TeContainerSlim>
      );
    });
  };

  return (
    <TeContainer column grow gap="xs" padding="xs" overflow="auto" alignItems="center">
      <Header text={t('domainRegistration.title')}>
        <TeButton
          text={t('domainRegistration.addDomain')}
          size="s"
          icon="add"
          teclick={() => openDomainAddEditModal(null)}
          disabled={!loaded}
        />
      </Header>

      <Section>
        <TeContainer column grow gap="s">
          {noContent ? renderNoContent() : renderDomains()}
        </TeContainer>
      </Section>

      {addEditDomainModalOpen && (
        <AddEditDomainModal
          domain={selectedDomain}
          open={addEditDomainModalOpen}
          onClose={handleAddEditDomainModalClose}
        />
      )}

      {selectedDomain && (
        <DeleteDomainModal
          open={deleteDomainModalOpen}
          onClose={handleDeleteDomainModalClose}
          domain={selectedDomain}
          identityProviderUrl={getIdentityProviderInfo(selectedDomain.identityProviderId ?? '')}
        />
      )}
    </TeContainer>
  );
};

const ButtonDivider = styled.div`
  height: 100%;
  border-right: ${border.secondary} 1px solid;
  padding-left: ${common.padding.xs2};
`;

const Divider = styled.div`
  height: ${common.size.xs2};
  width: 1px;
  background-color: ${border.primary};
  margin: 0 ${common.padding.xs};
`;

export default DomainRegistration;
